import { useMemo } from 'react';
import { CodeBlock } from '@/components/docs/CodeBlock';
import type { AdfElementNode } from '@/hooks/useAdfStandard';
import {
  buildAttributeString,
  getRawExampleValue,
  sortElementsByPriority,
  formatAttributesForXml,
  type AttributeConfig,
} from '@/lib/adf/xml-utils';

/**
 * Options for generating example XML
 */
export interface XmlPreviewOptions {
  currentDepth?: number;
  maxDepth?: number;
  requiredOnly?: boolean;
  includeTradeIn?: boolean;
  includeCoBuyer?: boolean;
}

/**
 * Generates example XML for an ADF element with realistic content.
 * Uses database-driven example values - no hardcoded placeholders.
 */
export function generateExampleXml(
  element: AdfElementNode,
  options: XmlPreviewOptions = {}
): string {
  const {
    currentDepth = 0,
    maxDepth = 10,
    requiredOnly = true,
    includeTradeIn = false,
    includeCoBuyer = false,
  } = options;

  const indent = '  '.repeat(currentDepth);
  const tagName = element.tag_name;
  const attributes = element.attributes as Record<string, AttributeConfig> | null;

  // Build attribute string using shared utility
  const attrString = formatAttributesForXml(
    buildAttributeString(attributes, {
      includeOptional: true,
      useMergeFields: false,
      elementSlug: element.slug,
    })
  );

  // Determine if we have children to render
  const hasChildren = element.children && element.children.length > 0;

  // For leaf elements: prefer raw example (preserves attributes from database)
  if (!hasChildren) {
    const rawExample = getRawExampleValue(element, { useAlternate: false });
    if (rawExample) {
      // Use the complete example XML from database (includes attributes)
      return `${indent}${rawExample}`;
    }
    // No example - build empty element with attributes
    return `${indent}<${tagName}${attrString}></${tagName}>`;
  }

  // Get example content for container elements that may have text content
  const rawExample = getRawExampleValue(element, { useAlternate: false });

  // Element with children - build opening tag
  let xml = `${indent}<${tagName}${attrString}>\n`;

  // Render children up to maxDepth
  if (hasChildren && currentDepth < maxDepth) {
    // Filter to required-only if flag is set, otherwise show all
    let childrenToRender = requiredOnly
      ? element.children.filter((c) => c.is_required)
      : element.children;

    // If requiredOnly but no required children, show first few optional ones
    if (requiredOnly && childrenToRender.length === 0 && element.children.length > 0) {
      childrenToRender = element.children.slice(0, 3);
    }

    // Sort children by priority
    const sortedChildren = sortElementsByPriority(childrenToRender);

    sortedChildren.forEach((child) => {
      // Check if this is a vehicle element and we need to render trade-in
      if (child.tag_name === 'vehicle' && child.is_repeatable && includeTradeIn) {
        // Render primary vehicle (buy)
        xml += renderElementWithOptions(child, currentDepth + 1, maxDepth, requiredOnly, {
          vehicleInterest: 'buy',
          useAlternate: false,
        });
        xml += '\n';
        // Render trade-in vehicle
        xml += renderElementWithOptions(child, currentDepth + 1, maxDepth, requiredOnly, {
          vehicleInterest: 'trade-in',
          useAlternate: true,
        });
        xml += '\n';
      }
      // Check if this is a customer element and we need to render co-buyer
      else if (child.tag_name === 'customer' && child.is_repeatable && includeCoBuyer) {
        // Render primary customer
        xml += renderElementWithOptions(child, currentDepth + 1, maxDepth, requiredOnly, {
          isCoBuyer: false,
          useAlternate: false,
        });
        xml += '\n';
        // Render co-buyer customer
        xml += renderElementWithOptions(child, currentDepth + 1, maxDepth, requiredOnly, {
          isCoBuyer: true,
          useAlternate: true,
        });
        xml += '\n';
      } else {
        xml += generateExampleXml(child, {
          currentDepth: currentDepth + 1,
          maxDepth,
          requiredOnly,
          includeTradeIn,
          includeCoBuyer,
        });
        xml += '\n';
      }
    });
  } else if (hasChildren) {
    // Past maxDepth - show truncation indicator
    xml += `${indent}  <!-- nested elements truncated at depth ${currentDepth} -->\n`;
  }

  xml += `${indent}</${tagName}>`;
  return xml;
}

/**
 * Renders an element with specific instance options (for trade-in/co-buyer scenarios)
 */
function renderElementWithOptions(
  element: AdfElementNode,
  depth: number,
  maxDepth: number,
  requiredOnly: boolean,
  options: {
    vehicleInterest?: 'buy' | 'trade-in';
    isCoBuyer?: boolean;
    useAlternate: boolean;
  }
): string {
  const indent = '  '.repeat(depth);
  const tagName = element.tag_name;
  const attributes = element.attributes as Record<string, AttributeConfig> | null;

  // Build attribute string with specific options
  const attrString = formatAttributesForXml(
    buildAttributeString(attributes, {
      includeOptional: true,
      useMergeFields: false,
      elementSlug: element.slug,
      vehicleInterest: options.vehicleInterest,
      isCoBuyer: options.isCoBuyer,
    })
  );

  const hasChildren = element.children && element.children.length > 0;

  // For leaf elements: prefer raw example (preserves attributes from database)
  if (!hasChildren) {
    const rawExample = getRawExampleValue(element, { useAlternate: options.useAlternate });
    if (rawExample) {
      return `${indent}${rawExample}`;
    }
    return `${indent}<${tagName}${attrString}></${tagName}>`;
  }

  // Element with children - build opening tag
  let xml = `${indent}<${tagName}${attrString}>\n`;

  if (hasChildren && depth < maxDepth) {
    let childrenToRender = requiredOnly
      ? element.children.filter((c) => c.is_required)
      : element.children;

    if (requiredOnly && childrenToRender.length === 0 && element.children.length > 0) {
      childrenToRender = element.children.slice(0, 3);
    }

    const sortedChildren = sortElementsByPriority(childrenToRender);

    sortedChildren.forEach((child) => {
      // Propagate the useAlternate AND isCoBuyer options to children
      xml += renderElementWithOptions(child, depth + 1, maxDepth, requiredOnly, {
        vehicleInterest: options.vehicleInterest,
        isCoBuyer: options.isCoBuyer,
        useAlternate: options.useAlternate,
      });
      xml += '\n';
    });
  } else if (hasChildren) {
    xml += `${indent}  <!-- nested elements truncated at depth ${depth} -->\n`;
  }

  xml += `${indent}</${tagName}>`;
  return xml;
}

interface AdfXmlPreviewProps {
  element: AdfElementNode;
  depth?: number;
  requiredOnly?: boolean;
  includeTradeIn?: boolean;
  includeCoBuyer?: boolean;
}

export function AdfXmlPreview({
  element,
  depth = 15,
  requiredOnly = false,
  includeTradeIn = false,
  includeCoBuyer = false,
}: AdfXmlPreviewProps) {
  const xmlCode = useMemo(() => {
    // Add XML declaration for root element
    const isRoot = element.tag_name === 'adf';
    const xml = generateExampleXml(element, {
      currentDepth: 0,
      maxDepth: depth,
      requiredOnly,
      includeTradeIn,
      includeCoBuyer,
    });

    if (isRoot) {
      return `<?xml version="1.0" encoding="UTF-8"?>\n<?adf version="1.0"?>\n${xml}`;
    }

    return xml;
  }, [element, depth, requiredOnly, includeTradeIn, includeCoBuyer]);

  return (
    <CodeBlock
      code={xmlCode}
      language="xml"
      title={`<${element.tag_name}> Example`}
      showLineNumbers
    />
  );
}
